import { useMemo, useState } from "react";
import moment from "moment";
import { useSubscription } from "react-stomp-hooks";
import { Area, AreaChart, Tooltip, XAxis, YAxis } from "recharts";
import Image from "next/image";
import LivePriceBox from "@/elements/LivePriceBox/LivePriceBox";
import NseOrBsePrice from "@/elements/NseOrBsePrice/NseOrBsePrice";
import { useAxios } from "@/hooks/useAxios";
import { useRouter } from "next/router";
import Actions from "@/elements/Actions";
import { Mixpanel } from "@/elements/Mixpanel";

const type = (ws, nse, bse) => {
    if (ws) {
        return ws;
    } else if (nse && nse !== 0) {
        return nse;
    } else {
        return bse;
    }
};

export default function OthersIndex({ name }) {
    const { data } = useAxios("/resources/stock-details/indian-main-indices-v2?futures=true");
    const indianIndices = data?.indianIndices ?? [];

    return (
        <>
            <p className="text-black text-base font-semibold leading-7 px-4 pb-4">Other indices</p>
            <div className="flex gap-3 lg:gap-4 w-full overflow-x-auto no-scrollbar swiper-no-swiping px-4 pb-8">
                {indianIndices
                    ?.filter((f) => f?.finCode != name)
                    ?.map((ele, i) => (
                        <IndicesCard ele={ele} key={i} />
                    ))}
            </div>
        </>
    );
}

export function IndicesCard({ ele }) {
    const router = useRouter();
    const [stockDetail, setStockDetail] = useState({});
    useSubscription(`/stock/live-price/${ele?.finCode}`, (res) => setStockDetail(JSON.parse(res.body)));

    const data = useMemo(() => ele?.stockPricesList ?? [], [ele?.stockPricesList]);
    return (
        <div
            id={`market-dices-${ele.compName?.toUpperCase()}`}
            onClick={() => {
                Mixpanel.track("card_clicked ", {
                    page: "Index_sdp",
                    card: ele.compName?.toLowerCase(),
                    fincode: ele?.finCode,
                    symbol: ele.nseSymbol ? ele.nseSymbol : ele.bseSymbol
                });
                router.push(Actions.toIndicesDetail(ele.nseSymbol ? ele.nseSymbol : ele.bseSymbol, ele.compName));
            }}
            className={`flex flex-col cursor-pointer justify-between min-w-[140px] rounded-xl p-3 border border-[#DFDFDF]`}
        >
            <div className="font-Inter text-xs font-semibold text-[#414141] flex w-full items-center gap-1">
                {ele?.flag && <Image src={ele?.flag} alt={ele.compName?.toUpperCase()} width={16} height={16} />}
                {ele.compName?.toUpperCase()}
            </div>
            <div className="font-Inter font-medium text-[10px] text-[#9D9D9D] my-1">
                {ele.nseSymbol ? "NSE" : "BSE"}
            </div>
            <AreaChart width={112} height={48} data={data} margin={{ top: 4, right: 0, left: 0, bottom: 8 }}>
                <defs>
                    <linearGradient id="green" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#84E9A0" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#84E9A0" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <defs>
                    <linearGradient id="red" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FDC4B7" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#FDC4B7" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <XAxis dataKey="x" hide />
                <YAxis hide domain={["dataMin", "dataMax"]} />
                <Area
                    type="monotone"
                    dataKey="y"
                    stroke={
                        type(stockDetail.ltpPrice, ele?.nseLtpPrice, ele?.bseLtpPrice) -
                            type(stockDetail.c, ele?.nseClosePrice, ele?.bseClosePrice) >=
                        0
                            ? "#26A649"
                            : "#EB4E2C"
                    }
                    fillOpacity={1}
                    fill={
                        type(stockDetail.ltpPrice, ele?.nseLtpPrice, ele?.bseLtpPrice) -
                            type(stockDetail.c, ele?.nseClosePrice, ele?.bseClosePrice) >=
                        0
                            ? "url(#green)"
                            : "url(#red)"
                    }
                />
            </AreaChart>

            <div className="flex flex-col">
                <LivePriceBox
                    ltpPrice={type(stockDetail.ltpPrice, ele?.nseLtpPrice, ele?.bseLtpPrice)}
                    textClass="font-Inter text-sm font-bold my-.5"
                />
                <NseOrBsePrice
                    nseLtpPrice={type(stockDetail.ltpPrice, ele?.nseLtpPrice, ele?.bseLtpPrice)}
                    nseClosePrice={type(stockDetail.c, ele?.nseClosePrice, ele?.bseClosePrice)}
                    textClass="font-Inter text-xs font-medium text-left"
                />
            </div>
        </div>
    );
}
